<template>
  <BaseFormLayout
    :title="`${editMode ? 'Edit' : 'Add'} ${supportModule.support} ${
      userModule.user
    }`"
    :nonFieldErrors="formErrors.non_field_errors"
  >
    <template #actions>
      <SecondaryButton
        id="supportUserReset"
        v-if="!editMode"
        @click="resetForm()"
        btnClass="mr-2 text-uppercase rounded-lg"
        name="Reset"
      >
      </SecondaryButton>
      <PrimaryButton
        id="supportUserSubmit"
        @click="submitForm()"
        :disabled="!isValid"
        name="Submit"
        btnClass="text-uppercase rounded-lg"
      >
      </PrimaryButton>
    </template>
    <template #content>
      <v-form
        v-model="isValid"
        ref="companyForm"
        :key="formCount"
        @submit.prevent="submitForm"
      >
        <v-card elevation="0" class="px-4 pt-4 pb-12 rounded-lg">
          <v-row class="ma-0">
            <v-col cols="12" class="pl-6">
              <span class="text-subtitle-1 font-weight-bold"
                >{{ userModule.user }} Information</span
              >
            </v-col>
            <v-col cols="8">
              <v-row class="ma-0">
                <v-col cols="4">
                  <InputField
                    id="username"
                    v-model="supportUserDetails.username"
                    label="User Name"
                    name="username"
                    :required="true"
                    :rules="[
                      (v) =>
                        !/[^a-zA-Z0-9_.-]/.test(v) ||
                        'Special characters are not allowed',
                    ]"
                    :error-messages="formErrors.username"
                    @input="delete formErrors.username"
                  />
                </v-col>

                <v-col cols="4">
                  <InputField
                    id="first_name"
                    v-model="supportUserDetails.first_name"
                    label="First Name"
                    name="first_name"
                    :required="true"
                    :error-messages="formErrors.first_name"
                    @input="delete formErrors.first_name"
                  />
                </v-col>
                <v-col cols="4">
                  <InputField
                    id="last_name"
                    v-model="supportUserDetails.last_name"
                    label="Last Name"
                    name="last_name"
                    :required="true"
                    :error-messages="formErrors.last_name"
                    @input="delete formErrors.last_name"
                  />
                </v-col>
                <v-col cols="4">
                  <InputField
                    id="contact_number"
                    type="number"
                    v-model.trim="supportUserDetails.contact_number"
                    label="Contact Number"
                    name="contact_number"
                    :required="true"
                    :rules="[
                      (val) =>
                        (val && val.length >= 9 && val.length <= 12) ||
                        'Contact number should be between 9 to 12 digits!',
                    ]"
                    :error-messages="formErrors.contact_number"
                    @input="delete formErrors.contact_number"
                  />
                </v-col>

                <v-col cols="4" lg="4">
                  <PasswordField
                    id="password"
                    label="Password"
                    name="password"
                    v-model="supportUserDetails.password"
                    :required="!editMode"
                    :error-messages="formErrors.password"
                    :rules="
                      supportUserDetails.password != null &&
                      supportUserDetails.confirm_password != null
                        ? passwordValidation
                        : []
                    "
                  />
                </v-col>
                <v-col cols="4" lg="4">
                  <PasswordField
                    id="confirm_password"
                    label="Confirm password"
                    name="confirm_password"
                    v-model="supportUserDetails.confirm_password"
                    :required="!editMode || !!supportUserDetails.password"
                    :rules="
                      supportUserDetails.password != null &&
                      supportUserDetails.confirm_password != null
                        ? passwordValidation
                        : []
                    "
                    :error-messages="formErrors.confirm_password"
                  />
                </v-col>
                <v-col cols="4">
                  <InputField
                    id="email"
                    v-model="supportUserDetails.email"
                    name="email"
                    label="Email"
                    :required="true"
                    :rules="
                      supportUserDetails.email
                        ? [
                            (v) =>
                              !v || emailValidation(v) || 'Invalid email id',
                          ]
                        : []
                    "
                    :error-messages="formErrors.email"
                    @input="delete formErrors.email"
                  />
                </v-col>
                <v-col cols="4">
                  <SelectField
                    id="preferred_timezone"
                    v-model="supportUserDetails.preferred_timezone"
                    name="preferred_timezone"
                    :itemsList="timezoneList"
                    itemText="name"
                    itemValue="id"
                    :rules="[(val) => !!val || 'Timezone is required']"
                    label="Timezone *"
                  />
                </v-col>
                <v-col cols="12" lg="4">
                  <SelectField
                    id="companies"
                    v-model="supportUserDetails.companies"
                    :items-list="companyList"
                    name="companies"
                    item-text="name"
                    item-value="id"
                    :label="`${companyModule.companyPlural}*`"
                    :multiple="true"
                    :rules="[
                      (v) =>
                        !!v.length > 0 ||
                        `${companyModule.companyPlural} are required`,
                    ]"
                    :error-messages="formErrors.companies"
                    @input="delete formErrors.companies"
                  />
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="4" class="d-flex justify-center">
              <ProfileUpload
                v-model="profileImage"
                @UploadProfile="updateProfile"
              />
            </v-col>
          </v-row>
        </v-card>
      </v-form>
    </template>
  </BaseFormLayout>
</template>

<script>
import { bus } from "@/main.js";
import { generatedFormData, emailValidation } from "@/utils/functions.js";
import BaseFormLayout from "@/components/LayoutComponents/BaseFormLayout.vue";
import {
  supportModule,
  userModule,
  companyModule,
} from "@/utils/moduleMapping";
import { timezones } from "@/utils/fieldConstants.js";

export default {
  components: {
    BaseFormLayout,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    editMode: {
      required: true,
    },
  },
  data() {
    return {
      timezoneList: timezones,
      supportModule,
      userModule,
      companyModule,
      emailValidation,
      viewPassword: false,
      oldPassword: false,
      fileInput: null,
      formCount: 0,
      isValid: true,
      supportUserDetails: {},
      formErrors: {},
      accountManagerSelectList: [],
      companyList: [],
      profileImage: null,
    };
  },
  computed: {
    passwordValidation() {
      return [
        this.supportUserDetails.password ==
          this.supportUserDetails.confirm_password || `Password does not match`,
      ];
    },
  },
  methods: {
    resetForm() {
      this.$refs.companyForm.reset();
    },
    getSupportUserObject(id) {
      this.$api.supportUser
        .getSupportUserObject(id)
        .then((res) => {
          if (res?.data?.profile_image) {
            this.profileImage = res.data.profile_image;
            delete res.data.profile_image;
          }
          this.supportUserDetails = res.data;
        })
        .catch((err) => {
          console.log(err);
        });
    },
    getCompanyList() {
      bus.$emit("showLoader", true);
      this.$api.company
        .getCompanyList({ limit: "all" })
        .then((res) => {
          this.companyList = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    updateProfile(image, isChangeable = false) {
      if (this.supportUserDetails.update_profile_image || isChangeable) {
        this.supportUserDetails.update_profile_image = true;
        if (image) {
          this.supportUserDetails.profile_image = image;
        } else {
          delete this.supportUserDetails.profile_image;
        }
      } else {
        this.supportUserDetails.update_profile_image = false;
        delete this.supportUserDetails.profile_image;
      }
    },
    submitForm() {
      if (!this.$refs.companyForm.validate()) {
        bus.$emit("showToastMessage", {
          message: "Please correct all the errors to submit the form!",
          color: "error",
        });
        return;
      }
      if (this.editMode) {
        if (
          this.supportUserDetails &&
          !this.supportUserDetails.update_profile_image
        ) {
          delete this.supportUserDetails.profile_image;
        }

        this.$api.supportUser
          .updateSupportUser({
            id: this.supportUserDetails.id,
            data: generatedFormData(this.supportUserDetails),
          })
          .then(() => {
            bus.$emit("showToastMessage", {
              message: "Support user information updated!",
              color: "success",
            });
            this.resetForm();
            this.$router.push({ name: "support-user" });
          })
          .catch((err) => {
            console.log(err);
            this.formErrors = err.data;
          });
      } else {
        this.$api.supportUser
          .createSupportUser(generatedFormData(this.supportUserDetails))
          .then(() => {
            bus.$emit("showToastMessage", {
              message: "Support user created!",
              color: "success",
            });
            this.resetForm();
            this.$router.push({ name: "support-user" });
          })
          .catch((err) => {
            this.formErrors = err.data;
          });
      }
    },
  },
  mounted() {
    if ("id" in this.$route.params) {
      this.getSupportUserObject(this.$route.params.id);
    }
    this.getCompanyList();
  },
};
</script>

<style>
.v-select__slot label {
  z-index: 2;
}
</style>
